import { Link, createFileRoute } from '@tanstack/react-router';
import { POSTS, POSTS_BY_SLUG } from '~/content/blog';
import { SITE_URL, buildHead } from '~/seo/site';

export const Route = createFileRoute('/blog/$slug')({
  head: ({ params }) => {
    const post = POSTS_BY_SLUG[params.slug];
    if (!post) {
      return buildHead({
        title: 'Post not found',
        description: 'This blog post does not exist.',
        path: `/blog/${params.slug}`,
        noindex: true,
      });
    }
    const head = buildHead({
      title: post.meta.title,
      description: post.meta.description,
      path: `/blog/${params.slug}`,
      type: 'article',
    });
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.meta.title,
      description: post.meta.description,
      author: { '@type': 'Organization', name: post.meta.author },
      publisher: {
        '@type': 'Organization',
        name: 'Prod Battle',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/icon-512.png` },
      },
      datePublished: post.meta.date,
      dateModified: post.meta.date,
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${params.slug}` },
      keywords: post.meta.primaryKeyword,
    };
    return {
      ...head,
      meta: [...head.meta, { 'script:ld+json': articleSchema }],
    };
  },
  component: BlogPost,
});

function BlogPost() {
  const { slug } = Route.useParams();
  const post = POSTS_BY_SLUG[slug];
  if (!post) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-20 text-center space-y-4">
        <h1 className="text-2xl font-bold">Post not found</h1>
        <Link to="/blog" className="underline">
          Back to blog
        </Link>
      </section>
    );
  }
  const Body = post.Body;
  return (
    <article className="mx-auto max-w-3xl px-4 py-12">
      <div className="mb-8">
        <Link
          to="/blog"
          className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          ← all posts
        </Link>
      </div>
      <header className="space-y-3 mb-10">
        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>{post.meta.category}</span>
          <span aria-hidden>·</span>
          <span>{post.meta.readMin} min read</span>
          <span aria-hidden>·</span>
          <time dateTime={post.meta.date}>{post.meta.date}</time>
        </div>
        <h1 className="text-3xl font-bold tracking-tight leading-tight">{post.meta.title}</h1>
        <p className="text-lg text-muted-foreground leading-relaxed">{post.meta.description}</p>
      </header>
      <div className="text-foreground/95">
        <Body />
      </div>

      <RelatedPosts currentSlug={slug} category={post.meta.category} />

      <footer className="mt-16 pt-8 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Try a beat battle on{' '}
          <Link to="/" className="text-foreground hover:text-primary underline">
            Prod Battle
          </Link>{' '}
          - free to play, real-time, ranked per genre.
        </p>
      </footer>
    </article>
  );
}

// Surface 3 other posts under the article. Prefer posts in the same category
// (tighter topical link), fill remainder with the next-newest posts.
// This component does the only real internal-linking lift on /blog/* - the
// audit found there was none before, which throttles how authority flows
// between our cluster pages.
function RelatedPosts({ currentSlug, category }: { currentSlug: string; category: string }) {
  const others = POSTS.filter((p) => p.meta.slug !== currentSlug);
  const sameCategory = others.filter((p) => p.meta.category === category);
  const fallback = others.filter((p) => p.meta.category !== category);
  const picks = [...sameCategory, ...fallback].slice(0, 3);
  if (picks.length === 0) return null;
  return (
    <section className="mt-16 pt-8 border-t border-border">
      <h2 className="mb-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        Related reading
      </h2>
      <ul className="space-y-3">
        {picks.map((p) => (
          <li key={p.meta.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: p.meta.slug }}
              className="block rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/50"
            >
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="font-semibold text-sm leading-snug">{p.meta.title}</span>
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {p.meta.readMin} min
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                {p.meta.description}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
